import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Zap, Clock, DollarSign, Layers, ChevronRight, ArrowLeft,
  Building2, Loader2, FolderOpen, Briefcase, CalendarDays,
  Check, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { id: 'Corporate_LMS', label: 'Corporate LMS', desc: 'Standard enterprise learning' },
  { id: 'Extended_Enterprise', label: 'Extended Enterprise', desc: 'Partners, customers, suppliers' },
  { id: 'Migration', label: 'Migration', desc: 'Migrate from another LMS' },
  { id: 'Upgrade', label: 'Upgrade', desc: 'Upgrade existing Seertech' },
];

const COMPLEXITIES = [
  { id: 'simple', label: 'Simple', desc: 'Core modules, minimal customization', color: 'border-emerald-300 bg-emerald-50' },
  { id: 'standard', label: 'Standard', desc: 'Full platform with integrations', color: 'border-blue-300 bg-blue-50' },
  { id: 'complex', label: 'Complex', desc: 'Multi-org, advanced integrations', color: 'border-violet-300 bg-violet-50' },
];

export default function QuickEstimatePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4ABFCF] to-[#0F2040] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-[#0F2040] font-['Outfit']">Quick Estimate</h1>
              <p className="text-xs text-slate-500">Get a ballpark in seconds — pick type, complexity, done.</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <QuickEstimateWizard />
      </div>
    </div>
  );
}

export function QuickEstimateWizard({ onClose, isDrawer = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [implType, setImplType] = useState('Corporate_LMS');
  const [complexity, setComplexity] = useState('standard');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/estimates/quick`, {
        implementation_type: implType,
        complexity,
        estimate_name: name || undefined,
        organization_name: orgName || undefined,
      }, { withCredentials: true });
      setResult(res.data);
      setStep(3);
      toast.success('Quick Estimate created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate estimate');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWorkspace = () => {
    if (onClose) onClose();
    navigate(`/workspace/${result.id}`);
  };

  const handleOpenEstimator = () => {
    if (onClose) onClose();
    navigate(`/estimator?load=${result.id}`);
  };

  return (
    <div className="space-y-6" data-testid="quick-estimate-wizard">
      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3].map(s => (
          <div
            key={s}
            className={`w-2 h-2 rounded-full transition-all ${
              s === step ? 'bg-[#4ABFCF] w-6' : s < step ? 'bg-[#4ABFCF]' : 'bg-slate-200'
            }`}
          />
        ))}
      </div>

      {/* Step 1: Type + Complexity */}
      {step === 1 && (
        <div className="space-y-5 animate-in fade-in" data-testid="quick-step-1">
          <div>
            <label className="text-xs font-semibold text-[#0F2040] mb-3 block">Implementation Type</label>
            <div className="grid grid-cols-2 gap-2">
              {TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setImplType(t.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    implType === t.id
                      ? 'border-[#4ABFCF] bg-[#4ABFCF]/5 ring-1 ring-[#4ABFCF]'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`quick-type-${t.id}`}
                >
                  <p className="text-xs font-semibold text-[#0F2040]">{t.label}</p>
                  <p className="text-[9px] text-slate-500 mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-[#0F2040] mb-3 block">Complexity</label>
            <div className="grid grid-cols-3 gap-2">
              {COMPLEXITIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setComplexity(c.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    complexity === c.id
                      ? `${c.color} ring-1 ring-[#4ABFCF]`
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`quick-complexity-${c.id}`}
                >
                  <p className="text-xs font-semibold text-[#0F2040]">{c.label}</p>
                  <p className="text-[9px] text-slate-500 mt-0.5">{c.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={() => setStep(2)} className="bg-[#0F2040] hover:bg-[#0F2040]/90" data-testid="quick-next-1">
              Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Optional details + generate */}
      {step === 2 && (
        <div className="space-y-5 animate-in fade-in" data-testid="quick-step-2">
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-[#4ABFCF]/10 text-[#0F2040] border-[#4ABFCF]/30 text-[9px]">
                  {TYPES.find(t => t.id === implType)?.label}
                </Badge>
                <Badge className="bg-slate-100 text-slate-700 text-[9px]">
                  {complexity.charAt(0).toUpperCase() + complexity.slice(1)}
                </Badge>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-medium mb-1 block">Estimate Name (optional)</label>
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g., Acme Corp Phase 1"
                  className="text-xs"
                  data-testid="quick-name"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-medium mb-1 block">Organization (optional)</label>
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <Input
                    value={orgName}
                    onChange={e => setOrgName(e.target.value)}
                    placeholder="e.g., Acme Corp"
                    className="text-xs"
                    data-testid="quick-org"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-[10px] text-slate-400 text-center">
            Auto-selects recommended components and calculates instantly.
          </p>

          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="bg-gradient-to-r from-[#4ABFCF] to-[#0F2040] hover:opacity-90"
              data-testid="quick-generate-btn"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1" />
              )}
              Generate Estimate
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && result && (
        <div className="space-y-5 animate-in fade-in" data-testid="quick-step-3">
          <div className="text-center mb-2">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-sm font-semibold text-[#0F2040]">{result.estimate_name}</h2>
            <p className="text-[10px] text-slate-500 mt-0.5">Auto-saved to your workspace</p>
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-1 text-slate-400 text-[9px] mb-1">
                  <Clock className="w-3 h-3" /> Total Effort
                </div>
                <p className="text-2xl font-bold text-[#0F2040] font-['Outfit']" data-testid="quick-result-effort">
                  {result.results?.total_effort?.toLocaleString() || 0}
                </p>
                <p className="text-[9px] text-slate-400">hours</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-1 text-slate-400 text-[9px] mb-1">
                  <DollarSign className="w-3 h-3" /> Total Cost
                </div>
                <p className="text-2xl font-bold text-[#4ABFCF] font-['Outfit']" data-testid="quick-result-cost">
                  ${result.results?.total_cost?.toLocaleString() || 0}
                </p>
                <p className="text-[9px] text-slate-400">estimated</p>
              </CardContent>
            </Card>
          </div>

          {/* Summary bar */}
          <Card className="border-slate-200">
            <CardContent className="p-3 flex items-center justify-between text-xs">
              <span className="text-slate-500 flex items-center gap-1">
                <Layers className="w-3 h-3" /> {result.component_count} components
              </span>
              <span className="text-slate-500">
                {result.implementation_type?.replace('_', ' ')} · {result.complexity}
              </span>
            </CardContent>
          </Card>

          {/* Cost range */}
          {result.results?.cost_range && (
            <div className="text-center">
              <p className="text-[10px] text-slate-400">
                Range: ${result.results.cost_range.low?.toLocaleString()} — ${result.results.cost_range.high?.toLocaleString()}
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              onClick={handleOpenWorkspace}
              className="bg-[#0F2040] hover:bg-[#0F2040]/90 w-full"
              data-testid="quick-open-workspace"
            >
              <FolderOpen className="w-3.5 h-3.5 mr-1.5" /> Open Workspace
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenEstimator}
              className="w-full"
              data-testid="quick-open-estimator"
            >
              <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Refine in Estimator
            </Button>
          </div>

          {/* Quick links */}
          <div className="flex items-center justify-center gap-4 pt-1">
            <button
              onClick={() => { if (onClose) onClose(); navigate(`/workspace/${result.id}/compare`); }}
              className="text-[10px] text-slate-400 hover:text-[#4ABFCF] transition-colors"
              data-testid="quick-goto-compare"
            >
              Compare
            </button>
            <button
              onClick={() => { if (onClose) onClose(); navigate(`/workspace/${result.id}/planner`); }}
              className="text-[10px] text-slate-400 hover:text-[#4ABFCF] transition-colors"
              data-testid="quick-goto-planner"
            >
              Plan Timeline
            </button>
            <button
              onClick={() => { if (onClose) onClose(); navigate(`/workspace/${result.id}/quote`); }}
              className="text-[10px] text-slate-400 hover:text-[#4ABFCF] transition-colors"
              data-testid="quick-goto-quote"
            >
              Generate Quote
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
